import React, { ChangeEvent, FC, memo, useEffect, useMemo } from 'react';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

import { toBN } from '@utils/formaters.utils';

import Button from '@components/common/Button';

import { Variant } from '@constants/common/ui.constants';

import useStyles from './styles.module';

type Props = {
  className?: string;
  classNameInput?: string;
  minValue: number;
  maxValue: number;
  count: number;
  inputValue: number;
  suffix?: string;
  onCount: (value: number) => void;
  onChange: (val: number) => void;
  onResetState?: () => void;
  disabled?: boolean;
  variant?: Variant;
};

const STEP = 1;

const Counter: FC<Props> = ({
  className,
  minValue,
  maxValue,
  count,
  onCount,
  classNameInput,
  inputValue,
  onChange,
  onResetState,
  suffix,
  disabled,
  variant = Variant.CircleGray,
}) => {
  const { classes, cx } = useStyles();

  const handleIncrement = () => {
    if (onResetState) {
      onResetState();
    }
    if (toBN(inputValue).isNaN()) {
      onCount(0);
      onChange(0);
      return;
    }

    if (toBN(minValue).lessThan(0) && toBN(count).plus(STEP).gt(maxValue)) {
      onCount(maxValue);
      onChange(maxValue);
      return;
    }

    if (toBN(maxValue).gt(0) && toBN(count).plus(STEP).gt(maxValue)) {
      onCount(maxValue);
      onChange(maxValue);
      return;
    }

    if (count >= maxValue) return;
    onCount(count + STEP);
    onChange(count + STEP);
  };
  const handleDecrement = () => {
    if (onResetState) {
      onResetState();
    }

    if (
      toBN(minValue).lessThan(0) &&
      toBN(count).minus(STEP).lessThan(minValue)
    ) {
      onCount(minValue);
      onChange(minValue);
      return;
    }

    if (
      toBN(inputValue).isNaN() ||
      toBN(count).minus(STEP).lessThan(minValue)
    ) {
      onCount(0);
      onChange(0);
      return;
    }

    if (count > minValue) {
      onCount(count - STEP);
      onChange(count - STEP);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.currentTarget.value, 10);

    if (
      (minValue < 0 && -val >= minValue && -val <= maxValue) ||
      toBN(val).isNaN()
    ) {
      onChange(-val);
    }

    if ((val >= minValue && val <= maxValue) || toBN(val).isNaN()) {
      onChange(val);
    }
  };

  const isDisabledIncrement = useMemo(
    () => inputValue >= maxValue,
    [inputValue, maxValue],
  );

  const isDisabledDecrement = useMemo(
    () => inputValue <= minValue,
    [inputValue, minValue],
  );

  useEffect(() => {
    onCount(Number(inputValue));
  }, [inputValue]);

  return (
    <div className={cx(classes.box, className)}>
      <div
        className={cx(classes.counterBtn, { [classes.suffixWrap]: !!suffix })}
      >
        {!disabled && (
          <Button
            onClick={handleDecrement}
            disabled={isDisabledDecrement}
            variant={variant}
          >
            <RemoveIcon className={classes.icon} />
          </Button>
        )}
        <div
          className={cx(classes.input, classNameInput, {
            [classes.suffixInput]: !!suffix,
          })}
        >
          <input
            value={toBN(inputValue).isNaN() ? '' : inputValue.toFixed(2)}
            onChange={handleChange}
            disabled={disabled}
          />
          {suffix && <div className={classes.suffix}>{suffix}</div>}
        </div>
        {!disabled && (
          <Button
            variant={variant}
            onClick={handleIncrement}
            disabled={isDisabledIncrement}
          >
            <AddIcon className={classes.icon} />
          </Button>
        )}
      </div>
    </div>
  );
};

export default memo(Counter);
